/* eslint-disable jira/filename-case */
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type { Collection } from '@jira/polaris__domain-collection/src/SrcViewResponse.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { wrapWithExperience } from '@jira/polaris__lib-analytics/src/createJpdExperience.tsx';
import { sendPendoTrackEvent } from '@jira/polaris__lib-analytics/src/sendPendoTrackEvent.tsx';
import { createErrorAnalytics } from '@jira/polaris__lib-errors/src/createErrorAnalytics.tsx';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { getJQLForCollection } from './getJQLForCollection.tsx';
import {
	createCollection as createCollectionRequest,
	fetchCollection,
	fetchAllCollections,
	deleteCollection as deleteCollectionRequest,
	updateCollection as updateCollectionRequest,
	cloneCollection,
} from './updateCollection.tsx';
import type { Props, State } from './State.types.tsx';

export type CollectionToCreate = {
	name: string;
	emoji?: string;
	color?: string;
	projectKeys?: string[];
	jql: string;
	ownerId: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
};

export const createCollection =
	({
		name,
		color,
		emoji,
		jql,
		ownerId,
		createAnalyticsEvent,
	}: CollectionToCreate): Action<State, Props, Promise<Collection | undefined>> =>
	async ({ setState, getState }, { onCollectionCreate }) => {
		try {
			return await wrapWithExperience(experience.collections.createCollection, async () => {
				const newCollection = await createCollectionRequest({
					name,
					color,
					emoji,
					jql,
				});

				const state = getState();

				setState({
					collections: {
						...state.collections,
						[newCollection.uuid]: {
							data: {
								...newCollection,
								ownerId,
							},
						},
					},
				});

				const actionSubjectAndAction = 'collection created';
				fireTrackAnalytics(createAnalyticsEvent({}), actionSubjectAndAction);

				sendPendoTrackEvent({ actionSubjectAndAction });

				// Ensure that onCollectionCreate always receives
				// a fresh reference
				onCollectionCreate?.({ ...newCollection });

				return newCollection;
			});
		} catch (error) {
			if (error instanceof Error) {
				fireErrorAnalytics(createErrorAnalytics('polaris.error.createCollectionFailed', error));
				throw error;
			}
		}
	};

export const initialize =
	(collections: Collection[]): Action<State> =>
	({ setState }) => {
		setState({
			collections: collections.reduce<State['collections']>((acc, collection) => {
				acc[collection.uuid] = { data: collection };
				return acc;
			}, {}),
			isLoading: false,
			error: undefined,
		});
	};

export const loadCollection =
	(collectionUUID: string, shouldIncludeViews?: boolean): Action<State, Props, Promise<void>> =>
	async ({ setState, getState }) => {
		const state = getState();
		const existingCollection = state.collections[collectionUUID] || {
			data: undefined,
		};

		if (existingCollection.isLoading) {
			return;
		}

		const loadExperience = shouldIncludeViews
			? experience.collections.loadCollectionViews
			: experience.collections.loadCollection;

		try {
			await wrapWithExperience(
				loadExperience,
				async () => {
					setState({
						collections: {
							...state.collections,
							[collectionUUID]: {
								...existingCollection,
								isLoading: true,
								error: undefined,
							},
						},
					});

					const newCollection = await fetchCollection(collectionUUID, shouldIncludeViews);

					setState({
						collections: {
							...state.collections,
							[collectionUUID]: {
								data: {
									...existingCollection.data,
									...newCollection,
								},
								isLoading: false,
							},
						},
					});

					return newCollection;
				},
				{
					ignorePermissionError: true,
					getMetadata: shouldIncludeViews
						? (collection) => ({
								collectionUUID,
								collectionViewsCount: collection.views?.length,
							})
						: undefined,
				},
			);
		} catch (error) {
			if (error instanceof Error) {
				fireErrorAnalytics(createErrorAnalytics('polaris.error.collectionLoadingFailed', error));
				setState({
					collections: {
						...state.collections,
						[collectionUUID]: {
							...existingCollection,
							isLoading: false,
							error,
						},
					},
				});
			}
		}
	};

export const loadCollections =
	(): Action<State, Props, Promise<Collection[] | undefined>> =>
	async ({ setState, dispatch }) => {
		try {
			return await wrapWithExperience(
				experience.collections.loadCollections,
				async () => {
					setState({
						collections: {},
						isLoading: true,
					});

					const collections = await fetchAllCollections();

					dispatch(initialize(collections));
					return collections;
				},
				{
					getMetadata: (collections) => ({
						collectionsCount: collections.length,
					}),
				},
			);
		} catch (error) {
			if (error instanceof Error) {
				fireErrorAnalytics(
					createErrorAnalytics('polaris.error.allCollectionsLoadingFailed', error),
				);
				setState({
					isLoading: false,
					error,
				});
			}
		}
		return undefined;
	};

export const deleteCollection =
	(
		collectionUUID: string,
		{ deleteOnlyOnClientSide }: { deleteOnlyOnClientSide: boolean } = {
			deleteOnlyOnClientSide: false,
		},
	): Action<State, Props> =>
	async ({ setState, getState }, { onCollectionDelete }) => {
		const state = getState();

		try {
			await wrapWithExperience(experience.collections.deleteCollection, async () => {
				const { [collectionUUID]: _, ...collections } = state.collections;

				if (!deleteOnlyOnClientSide) {
					await deleteCollectionRequest(collectionUUID);
				}

				setState({
					collections,
				});

				onCollectionDelete?.(collectionUUID);
			});
		} catch (error) {
			if (error instanceof Error) {
				fireErrorAnalytics(createErrorAnalytics('polaris.error.collectionDeletionFailed', error));
			}
			throw error;
		}
	};

export type CollectionToUpdate = Partial<
	CollectionToCreate & {
		projectKeys?: string[];
		filtersJql?: string;
	}
>;

export const updateCollection =
	(collectionUUID: string, collection: Partial<CollectionToUpdate>): Action<State, Props> =>
	async ({ setState, getState }, { onCollectionUpdate }) => {
		const state = getState();
		const existingCollection = state.collections[collectionUUID];
		const data = existingCollection?.data;

		if (!data) {
			return;
		}

		try {
			await wrapWithExperience(experience.collections.updateCollection, async () => {
				const { name, color, emoji, projectKeys, filtersJql } = collection;
				const jql =
					projectKeys !== undefined || filtersJql !== undefined
						? getJQLForCollection(projectKeys ?? data.projectKeys, filtersJql ?? data.filtersJql)
						: data.jql;

				const updatedCollection = {
					name: name || data.name,
					color: 'color' in collection ? color : data.color,
					emoji: 'emoji' in collection ? emoji : data.emoji,
					jql,
				};

				await updateCollectionRequest(collectionUUID, updatedCollection);

				const updatedCollectionData: Collection = {
					...data,
					...updatedCollection,
					projectKeys: projectKeys ?? data.projectKeys,
					filtersJql: filtersJql ?? data.filtersJql,
				};

				setState({
					collections: {
						...state.collections,
						[collectionUUID]: {
							...existingCollection,
							data: updatedCollectionData,
						},
					},
				});

				// Ensure that onCollectionUpdate always receives
				// a fresh reference
				onCollectionUpdate?.({ ...updatedCollectionData });
			});
		} catch (error) {
			if (error instanceof Error) {
				fireErrorAnalytics(createErrorAnalytics('polaris.error.collectionUpdateFailed', error));
			}
			throw error;
		}
	};

export const copyCollection =
	(
		collectionUUID: string,
		newCollectionName: string,
		newCollectionOwnerId: string | null,
		collectionTemporaryData?: Collection,
	): Action<State, undefined, Promise<string | undefined>> =>
	async ({ setState, getState }) => {
		try {
			const state = getState();
			const collectionToCopy = state.collections[collectionUUID]?.data || collectionTemporaryData;

			if (!collectionToCopy || !newCollectionOwnerId) {
				return;
			}

			return await wrapWithExperience(experience.collections.copyCollection, async () => {
				const { uuid } = await cloneCollection(collectionUUID, newCollectionName);

				setState({
					collections: {
						...state.collections,
						[uuid]: {
							isLoading: false,
							error: undefined,
							data: {
								...collectionToCopy,
								name: newCollectionName,
								ownerId: newCollectionOwnerId,
								uuid,
							},
						},
					},
				});
				return uuid;
			});
		} catch (error) {
			if (error instanceof Error) {
				fireErrorAnalytics(createErrorAnalytics('polaris.error.copyCollectionFailed', error));
				throw error;
			}
		}
	};

export const getCollectionData =
	(collectionUUID: string): Action<State, void, Promise<Collection | undefined>> =>
	async ({ getState }) => {
		const state = getState();
		const existingCollection = state.collections[collectionUUID] || {
			data: undefined,
		};

		if (existingCollection.data?.views !== undefined) {
			return existingCollection.data;
		}

		try {
			return await wrapWithExperience(experience.collections.loadCollection, async () => {
				const collection = await fetchCollection(collectionUUID, true);
				return collection;
			});
		} catch (error) {
			if (error instanceof Error) {
				fireErrorAnalytics(createErrorAnalytics('polaris.error.collectionLoadingFailed', error));
			}
		}
	};

export const syncStateWithCollectionStoreAfterViewDelete =
	(collectionUUID: string, viewUUID: string): Action<State, void, void> =>
	({ getState, setState }) => {
		const state = getState();
		const collection = state.collections && state.collections[collectionUUID];

		if (!collection || !collection.data) {
			return;
		}

		const currentViewsCount = collection.data.viewsCount ?? 0;
		const viewsCount = currentViewsCount > 0 ? currentViewsCount - 1 : 0;

		const views = collection.data.views?.filter((view) => view.uuid !== viewUUID);

		setState({
			collections: {
				...state.collections,
				[collectionUUID]: {
					data: {
						...collection.data,
						views,
						viewsCount,
					},
				},
			},
		});
	};
